/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Pure derivation helpers for payment allocation. No Formily / React.
 */
import _ from 'lodash';

import { toCents, fromCents } from 'utils/claims/postPayment';

type ClaimEditWriteOff = PatientPayment.ClaimEditWriteOff;
type ClaimEdit = PatientPayment.ClaimEdit;
type ClaimEdits = PatientPayment.ClaimEdits;
type PaymentMeta = PatientPayment.PaymentMeta;
type DerivedClaimRow = PatientPayment.DerivedClaimRow;
type DerivedAccountGroup = PatientPayment.DerivedAccountGroup;
type DerivedAllocationState = PatientPayment.DerivedAllocationState;

const clampToNonNegative = (value: number): number => _.clamp(value, 0, Number.POSITIVE_INFINITY);

const buildInitialClaimEdits = (parsedModel?: PatientPayment.IAllocationModel | null): ClaimEdits => {
	const edits: ClaimEdits = {};
	_.forEach(parsedModel?.claimsTable || [], (account) => {
		_.forEach(account.children || [], (child) => {
			edits[child.claimId] = {
				allocatedAmount: _.toNumber(child.allocatedAmount) || 0,
				writeOff: {
					amount: _.toNumber(child.writeOff?.amount) || 0,
					reasonId: child.writeOff?.reasonId ?? null,
				},
			};
		});
	});
	return edits;
};

const getFormAllocatedCents = (edits: ClaimEdits): number => _.reduce(edits, (total, edit) => total + toCents(edit?.allocatedAmount), 0);

const getInitialFormAllocatedCents = (initialEdits: ClaimEdits): number =>
	_.reduce(initialEdits, (total, edit) => total + toCents(edit?.allocatedAmount), 0);

const buildCollectedPRForRow = (
	claim: PatientPayment.IClaimInfo,
	allocatedAmount: number,
	paymentMeta: PaymentMeta,
): { total: number; details: PatientPayment.IAllocationsDetails[] } => {
	const others = _.filter(claim.collectedPR?.details || [], (detail) => detail?.id !== paymentMeta.paymentReconciliationId);
	const details: PatientPayment.IAllocationsDetails[] = _.map(others, (detail) => ({
		id: detail.id,
		amount: _.toNumber(detail.amount) || 0,
		paymentMethod: detail.paymentMethod,
		referenceNumber: detail.referenceNumber,
	}));
	if (allocatedAmount > 0) {
		details.push({
			id: paymentMeta.paymentReconciliationId,
			amount: allocatedAmount,
			paymentMethod: paymentMeta.paymentMethod || 'Cash',
			referenceNumber: paymentMeta.referenceNumber,
		});
	}
	const total = _.sumBy(details, (detail) => _.toNumber(detail.amount) || 0);
	return { total, details };
};

const deriveAllocationState = ({
	parsedModel,
	edits,
	initialEdits,
	paymentMeta,
}: {
	parsedModel?: PatientPayment.IAllocationModel | null;
	edits: ClaimEdits;
	initialEdits: ClaimEdits;
	paymentMeta: PaymentMeta;
}): DerivedAllocationState => {
	const formAllocatedCents = getFormAllocatedCents(edits);
	const initialFormAllocatedCents = getInitialFormAllocatedCents(initialEdits);
	const unallocatedCents = toCents(paymentMeta.initialUnallocatedAmount);
	const formCapCents = initialFormAllocatedCents + unallocatedCents;

	const groups: DerivedAccountGroup[] = _.map(parsedModel?.claimsTable || [], (account) => {
		const rows: DerivedClaimRow[] = _.map(account.children || [], (child) => {
			const edit = edits[child.claimId] ?? {
				allocatedAmount: 0,
				writeOff: { amount: 0, reasonId: null },
			};
			const allocatedAmount = _.toNumber(edit.allocatedAmount) || 0;
			const writeOff: ClaimEditWriteOff = {
				amount: _.toNumber(edit.writeOff?.amount) || 0,
				reasonId: edit.writeOff?.reasonId ?? null,
			};
			const claimResponseAvailable = !!child.claimResponseAvailable;
			const patientResponsibility = _.toNumber(child.patientResponsibility) || 0;
			const billedAmount = _.toNumber(child.billedAmount) || 0;

			const collectedPR = buildCollectedPRForRow(child, allocatedAmount, paymentMeta);
			const otherCollectorTotal = clampToNonNegative(collectedPR.total - allocatedAmount);

			const pendingPR = claimResponseAvailable ? patientResponsibility - collectedPR.total - writeOff.amount : 0;

			const maxWriteOff = claimResponseAvailable ? clampToNonNegative(patientResponsibility - collectedPR.total) : 0;

			const allocatedCents = toCents(allocatedAmount);
			const otherFormAllocationsCents = formAllocatedCents - allocatedCents;
			const rowMaxByFormCents = clampToNonNegative(formCapCents - otherFormAllocationsCents);
			const rowMaxByForm = fromCents(rowMaxByFormCents);
			const maxByClaim = claimResponseAvailable
				? pendingPR + allocatedAmount
				: clampToNonNegative(billedAmount - otherCollectorTotal);
			const cappedMax = clampToNonNegative(_.min([rowMaxByForm, maxByClaim]) ?? 0);
			const applyAmount = clampToNonNegative(_.min([cappedMax, allocatedAmount + clampToNonNegative(pendingPR)]) ?? 0);

			return {
				key: child.claimId,
				claimInfo: child,
				allocatedAmount,
				writeOff,
				collectedPR,
				pendingPR,
				maxWriteOff,
				cappedMax,
				applyAmount,
				hideApplyButton: !claimResponseAvailable,
				claimResponseAvailable,
				disableAllocate: cappedMax <= 0,
				disableWriteOff: !claimResponseAvailable || maxWriteOff <= 0,
			};
		});

		const outstandingPR = _.sumBy(rows, (row) => row.pendingPR);

		return {
			key: account.key,
			accountDetails: account.accountDetails,
			outstandingPR,
			rows,
		};
	});

	return {
		groups,
		formAllocatedCents,
		initialFormAllocatedCents,
	};
};

const claimEditsEqual = (a?: ClaimEdit, b?: ClaimEdit): boolean => {
	const aAlloc = _.toNumber(a?.allocatedAmount) || 0;
	const bAlloc = _.toNumber(b?.allocatedAmount) || 0;
	const aWriteOff = _.toNumber(a?.writeOff?.amount) || 0;
	const bWriteOff = _.toNumber(b?.writeOff?.amount) || 0;
	const aReason = a?.writeOff?.reasonId ?? null;
	const bReason = b?.writeOff?.reasonId ?? null;
	return aAlloc === bAlloc && aWriteOff === bWriteOff && aReason === bReason;
};

const getChangedClaims = (edits: ClaimEdits, initialEdits: ClaimEdits): string[] => {
	const claimIds = _.union(_.keys(edits), _.keys(initialEdits));
	return _.filter(claimIds, (claimId) => !claimEditsEqual(edits[claimId], initialEdits[claimId]));
};

const getAllocationDeltaCents = (edits: ClaimEdits, initialEdits: ClaimEdits): number => {
	const claimIds = _.union(_.keys(edits), _.keys(initialEdits));
	return _.reduce(
		claimIds,
		(total, claimId) => {
			const currentCents = toCents(edits[claimId]?.allocatedAmount || 0);
			const initialCents = toCents(initialEdits[claimId]?.allocatedAmount || 0);
			return total + (currentCents - initialCents);
		},
		0,
	);
};

const hasClaimEditChanges = (edits: ClaimEdits, initialEdits: ClaimEdits): boolean => getChangedClaims(edits, initialEdits).length > 0;

export {
	buildCollectedPRForRow,
	buildInitialClaimEdits,
	claimEditsEqual,
	deriveAllocationState,
	getAllocationDeltaCents,
	getChangedClaims,
	getFormAllocatedCents,
	getInitialFormAllocatedCents,
	hasClaimEditChanges,
};
